/**
 * Cashflow agent chat — proposals with Accept / Decline / Snooze.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ConversationFeed from '../common/ConversationFeed';
import ProposalCard from './ProposalCard';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import AuraFocusStrip from '../advice/AuraFocusStrip';
import { useAdviceSync } from '../../context/AdviceSyncContext';

const AuraCashflowChat = ({
  user,
  summary,
  chatSeed,
  onChatSeedConsumed,
  onDataChanged,
  proposalsRefreshKey = 0,
}) => {
  const [cards, setCards] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const feedRef = useRef(null);
  const seedHandled = useRef(false);
  const { applySyncPayload, refreshAfterChat } = useAdviceSync();

  const loadProposals = async () => {
    try {
      const res = await axios.get('/api/cashflow/proposals', { timeout: 8000 });
      setProposals(res.data.proposals || []);
    } catch (e) {
      console.error('Proposals load failed (non-blocking):', e);
      setProposals([]);
    }
  };

  useEffect(() => {
    const hasData = summary?.has_data === true;
    const intro = hasData
      ? "I can read your cashflow summary, visions, and accounts. Ask anything — I'll suggest changes as proposals you accept."
      : 'Use the entry form first to add income and expenses. I never change your data without your explicit accept on a proposal.';
    setCards([
      {
        id: 'welcome',
        type: 'aura_reflection',
        title: 'Ask Aura',
        content: `Your cashflow advisor.\n\n${intro}\n\n**Propose, don't dispose** — I explain and suggest; you decide.`,
        timestamp: new Date().toISOString(),
      },
    ]);
    loadProposals();
  }, [summary?.has_data]);

  useEffect(() => {
    if (chatSeed && !seedHandled.current) {
      seedHandled.current = true;
      sendMessage(chatSeed);
      onChatSeedConsumed?.();
    }
  }, [chatSeed]);

  useEffect(() => {
    if (proposalsRefreshKey > 0) {
      loadProposals();
    }
  }, [proposalsRefreshKey]);

  const sendMessage = async (text) => {
    const userMessage = (text || inputMessage).trim();
    if (!userMessage || loading) return;

    setCards((prev) => [
      ...prev,
      {
        id: `user_${Date.now()}`,
        type: 'user_message',
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post('/api/cashflow/chat', { message: userMessage });
      if (response.data.success && response.data.cards) {
        const newCards = response.data.cards.filter((c) => c.type !== 'proposal');
        const newProposals = response.data.cards
          .filter((c) => c.type === 'proposal' && c.proposal)
          .map((c) => c.proposal);
        setCards((prev) => [...prev, ...newCards]);
        if (newProposals.length) {
          setProposals((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            return [...prev, ...newProposals.filter((p) => !ids.has(p.id))];
          });
        }
      }
      if (response.data?.advice_sync) {
        applySyncPayload(response.data.advice_sync);
      }
      await refreshAfterChat();
      await loadProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      setCards((prev) => [
        ...prev,
        {
          id: `error_${Date.now()}`,
          type: 'aura_reflection',
          title: 'Something went wrong',
          content:
            typeof detail === 'string'
              ? detail
              : 'Please try again.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleProposalResolved = async () => {
    await loadProposals();
    onDataChanged?.();
  };

  const activeProposals = proposals.filter((p) => p.status === 'proposed');

  return (
    <div className="h-full min-h-0 flex flex-col surface-dark">
      <div className="flex items-center gap-2 mb-4 shrink-0 pb-4 border-b border-[color:var(--text-secondary)] border-opacity-30">
        <ChatBubbleLeftRightIcon className="w-5 h-5 text-secondary" />
        <div>
          <h2 className="font-display text-h2 text-primary">Ask Aura</h2>
          <p className="text-body text-secondary">Cashflow advisor — budgeting and proposals</p>
        </div>
      </div>

      <AuraFocusStrip className="mb-3 shrink-0" compact />

      {activeProposals.length > 0 && (
        <div className="mb-3 shrink-0 max-h-48 overflow-y-auto">
          {activeProposals.map((p) => (
            <ProposalCard
              key={p.id}
              proposal={p}
              onResolved={() => handleProposalResolved()}
            />
          ))}
        </div>
      )}

      <div className="flex-1 min-h-[240px] overflow-y-auto mt-2 [&_.aura-card]:light-surface [&_.aura-card]:bg-bone-raised [&_.aura-card]:border-hairline">
        <ConversationFeed
          ref={feedRef}
          cards={cards}
          user={user}
          loading={loading}
          section="cashflow"
        />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
        className="mt-4 flex gap-2 shrink-0 pt-4 border-t border-[color:var(--text-secondary)] border-opacity-30"
      >
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Can I afford $200 dining out?"
          className="flex-1 px-3 py-2 rounded-md border border-[color:var(--text-secondary)] border-opacity-40 bg-pine/30 text-primary placeholder:text-secondary focus:outline-none focus:ring-2 focus:ring-[color:var(--text-primary)] text-body"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !inputMessage.trim()}
          className="px-4 py-2 rounded-md bg-bone text-pine-deep font-medium text-body disabled:opacity-50 transition-opacity duration-300"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default AuraCashflowChat;
